/**
 * Presence Store
 * Tiene traccia dello stato online/away di tutti gli utenti dell'organizzazione
 * Popolato dal presenceService tramite Supabase Realtime Presence
 * Include anche lo stato personalizzato (emoji + testo)
 */

import { create } from 'zustand';
import { supabase } from '../supabase';

export type PresenceStatus = 'online' | 'away';

export interface PresenceEntry {
  status: PresenceStatus;
  lastSeen: string; // ISO timestamp
}

export interface CustomStatus {
  emoji: string;
  text: string;
  expires_at?: string | null; // ISO timestamp
}

interface PresenceState {
  presenceMap: Record<string, PresenceEntry>; // userId → PresenceEntry
  customStatuses: Record<string, CustomStatus>; // userId → CustomStatus
  setPresenceMap: (map: Record<string, PresenceEntry>) => void;
  getPresence: (userId: string) => PresenceEntry | null;
  getCustomStatus: (userId: string) => CustomStatus | null;
  fetchCustomStatuses: (organizationId: string) => Promise<void>;
  setMyCustomStatus: (emoji: string, text: string, expiresAt?: string | null) => Promise<void>;
  clearMyCustomStatus: () => Promise<void>;
  clear: () => void;
}

export const usePresenceStore = create<PresenceState>((set, get) => ({
  presenceMap: {},
  customStatuses: {},

  setPresenceMap: (map) => set({ presenceMap: map }),

  getPresence: (userId) => get().presenceMap[userId] || null,

  getCustomStatus: (userId) => {
    const status = get().customStatuses[userId];
    if (!status) return null;
    // Check expiration
    if (status.expires_at && new Date(status.expires_at) < new Date()) return null;
    return status;
  },

  fetchCustomStatuses: async (organizationId: string) => {
    const { data } = await supabase
      .from('user_profiles')
      .select('id, custom_status_emoji, custom_status_text, custom_status_expires_at')
      .eq('organization_id', organizationId)
      .not('custom_status_text', 'is', null);

    const map: Record<string, CustomStatus> = {};
    (data || []).forEach((u: any) => {
      if (u.custom_status_text || u.custom_status_emoji) {
        // Skip expired
        if (u.custom_status_expires_at && new Date(u.custom_status_expires_at) < new Date()) return;
        map[u.id] = {
          emoji: u.custom_status_emoji || '',
          text: u.custom_status_text || '',
          expires_at: u.custom_status_expires_at,
        };
      }
    });
    set({ customStatuses: map });
  },

  setMyCustomStatus: async (emoji: string, text: string, expiresAt?: string | null) => {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) return;
    const userId = userData.user.id;

    // Optimistic update
    set(state => ({
      customStatuses: {
        ...state.customStatuses,
        [userId]: { emoji, text, expires_at: expiresAt || null },
      },
    }));

    await supabase.from('user_profiles').update({
      custom_status_emoji: emoji || null,
      custom_status_text: text || null,
      custom_status_expires_at: expiresAt || null,
    }).eq('id', userId);
  },

  clearMyCustomStatus: async () => {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) return;
    const userId = userData.user.id;

    set(state => {
      const next = { ...state.customStatuses };
      delete next[userId];
      return { customStatuses: next };
    });

    await supabase.from('user_profiles').update({
      custom_status_emoji: null,
      custom_status_text: null,
      custom_status_expires_at: null,
    }).eq('id', userId);
  },

  clear: () => set({ presenceMap: {}, customStatuses: {} }),
}));
